import axios from 'axios';
import { WEB_API_BASE_URL, API_BASE_URL } from '../config/config';
import { handleSessionExpired } from '../utils/sessionExpiredHandler';

/**
 * Follow-Up & Contest Service
 * Calls PublicFollowUpContest endpoints on the Web API
 */
class FollowUpContestService {
  private client = axios.create({
    baseURL: WEB_API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add auth token interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle 401
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );
  }

  // FollowUp
  async submitFollowUp(request: {
    FormName: string;
    FormUID: string;
    FormNumber: string;
    Message: string;
    TakeCDEAFrom: string;
    CDEAColumnName: string;
  }) {
    const response = await this.client.post('/PublicFollowUpContest/SubmitFollowUp', request);
    return response.data;
  }

  async sendFollowUpMessage(request: { ParentUID: string; Message: string }) {
    const response = await this.client.post('/PublicFollowUpContest/SendFollowUpMessage', request);
    return response.data;
  }

  async getFollowUpMessages(uid: string) {
    const response = await this.client.get(`/PublicFollowUpContest/GetFollowUpMessages?uid=${encodeURIComponent(uid)}`);
    return response.data;
  }

  async getFollowUpList(request: {
    MaximumRows?: number;
    StartRowIndex?: number;
    Status?: string;
    FormName?: string;
    FormNumber?: string;
    StartDate?: string;
    EndDate?: string;
  }) {
    const response = await this.client.post('/PublicFollowUpContest/GetFollowUpList', request);
    return response.data;
  }

  async closeFollowUp(uid: string) {
    const response = await this.client.post('/PublicFollowUpContest/CloseFollowUp', { UID: uid });
    return response.data;
  }

  async checkFollowUpExists(formUID: string) {
    const response = await this.client.get(`/PublicFollowUpContest/CheckFollowUpExists?formUID=${encodeURIComponent(formUID)}`);
    return response.data;
  }

  // Contest
  async submitContest(request: {
    FormName: string;
    FormUID: string;
    FormNumber: string;
    Message: string;
    TakeCDEAFrom: string;
    CDEAColumnName: string;
  }) {
    const response = await this.client.post('/PublicFollowUpContest/SubmitContest', request);
    return response.data;
  }

  async sendContestMessage(request: { ParentUID: string; Message: string }) {
    const response = await this.client.post('/PublicFollowUpContest/SendContestMessage', request);
    return response.data;
  }

  async getContestMessages(uid: string) {
    const response = await this.client.get(`/PublicFollowUpContest/GetContestMessages?uid=${encodeURIComponent(uid)}`);
    return response.data;
  }

  async getContestList(request: {
    MaximumRows?: number;
    StartRowIndex?: number;
    Status?: string;
    FormName?: string;
    FormNumber?: string;
    StartDate?: string;
    EndDate?: string;
  }) {
    const response = await this.client.post('/PublicFollowUpContest/GetContestList', request);
    return response.data;
  }

  async closeContest(uid: string) {
    const response = await this.client.post('/PublicFollowUpContest/CloseContest', { UID: uid });
    return response.data;
  }

  async checkContestExists(formUID: string) {
    const response = await this.client.get(`/PublicFollowUpContest/CheckContestExists?formUID=${encodeURIComponent(formUID)}`);
    return response.data;
  }

  // File Upload
  async uploadChatFile(file: File, messageType: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('messageType', messageType);

    const token = localStorage.getItem('authToken');
    const response = await this.client.post('/PublicFollowUpContest/UploadChatFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  }

  getDownloadUrl(filePath: string): string {
    return `${WEB_API_BASE_URL}/PublicFollowUpContest/DownloadChatFile?filePath=${encodeURIComponent(filePath)}`;
  }

  // FormGridConfiguration - uses MobileService endpoint (no SessionValidationFilter needed)
  async getFormGridConfiguration(formName: string) {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/CustomerPortal/GetFormGridConfiguration?FormName=${encodeURIComponent(formName)}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  }
}

export default new FollowUpContestService();
